'use client';

import { useRef, useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Icon from '@/components/ui/AppIcon';

if (typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
}

import MobilePricingTiers from './MobilePricingTiers';

// Plan Data separated by Category
const plans = {
    development: [
        {
            id: 'basic',
            name: 'Basic',
            price: '₹30,000',
            subtitle: 'Perfect for startups',
            desc: 'Essential web presence with modern design.',
            popular: false,
            gradient: 'from-slate-700 to-slate-900',
            btnStyle: 'border-2 border-slate-200 hover:border-indigo-600 text-slate-700 hover:text-indigo-600'
        },
        {
            id: 'pro',
            name: 'Professional',
            price: '₹80,000',
            subtitle: 'Most Popular',
            desc: 'Robust solution for growing businesses.',
            popular: true,
            gradient: 'from-indigo-600 to-violet-600',
            btnStyle: 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-200'
        },
        {
            id: 'ent',
            name: 'Enterprise',
            price: 'Contact Us',
            subtitle: 'Full Scale',
            desc: 'Complete digital transformation.',
            popular: false,
            gradient: 'from-slate-800 to-black',
            btnStyle: 'bg-slate-900 text-white hover:bg-slate-800'
        }
    ],
    growth: [
        {
            id: 'seo',
            name: 'SEO Starter',
            price: '₹10,000',
            subtitle: 'Organic Growth',
            desc: 'Boost your rankings and local visibility.',
            popular: false,
            gradient: 'from-blue-600 to-cyan-600',
            btnStyle: 'border-2 border-blue-200 hover:border-blue-600 text-slate-700 hover:text-blue-600'
        },
        {
            id: 'gmb',
            name: 'GMB Management',
            price: '₹10,000',
            subtitle: 'Local Dominance',
            desc: 'Optimize your Google Business Profile.',
            popular: true,
            gradient: 'from-violet-600 to-fuchsia-600',
            btnStyle: 'bg-violet-600 text-white hover:bg-violet-700 shadow-lg shadow-violet-200'
        }
    ]
};

// Features Data
const featuresData = {
    development: [
        { name: 'Custom Design', basic: true, pro: true, ent: true },
        { name: 'Responsive Layout', basic: true, pro: true, ent: true },
        { name: 'SEO Foundation', basic: true, pro: true, ent: true },
        { name: 'CMS Integration', basic: false, pro: true, ent: true },
        { name: 'E-commerce', basic: false, pro: true, ent: true },
        { name: 'API Integration', basic: false, pro: false, ent: true },
        { name: 'Priority Support', basic: false, pro: true, ent: true },
    ],
    growth: [
        { name: 'Keyword Research', seo: true, gmb: false },
        { name: 'On-Page Optimization', seo: true, gmb: false },
        { name: 'Profile Setup & Verification', seo: false, gmb: true },
        { name: 'Review Management', seo: false, gmb: true },
        { name: 'Weekly Posting', seo: false, gmb: true },
        { name: 'Monthly Reporting', seo: true, gmb: true },
        { name: 'Competitor Analysis', seo: true, gmb: true },
    ]
};

const PricingTiers = () => {
    const containerRef = useRef<HTMLElement>(null);
    const searchParams = useSearchParams();
    const [category, setCategory] = useState<'development' | 'growth'>('development');
    const [revealedPrices, setRevealedPrices] = useState<Record<string, boolean>>({});

    useEffect(() => {
        const catParam = searchParams.get('category');
        if (catParam === 'growth') {
            setCategory('growth');
        } else if (catParam === 'development') {
            setCategory('development');
        }
    }, [searchParams]);

    const triggerReveal = (id: string, e: React.MouseEvent) => {
        setRevealedPrices(prev => ({ ...prev, [id]: true }));

        const target = e.currentTarget;
        const particles = target.querySelectorAll(`.price-burst-${id} div`);

        gsap.fromTo(particles,
            { x: 0, y: 0, opacity: 1, scale: 0.5 },
            {
                x: (i) => Math.cos((i / 6) * Math.PI * 2) * 60,
                y: (i) => Math.sin((i / 6) * Math.PI * 2) * 60,
                opacity: 0,
                scale: 0,
                duration: 0.6,
                ease: 'power2.out'
            }
        );

        gsap.fromTo(target.querySelector('span.text-4xl'),
            { scale: 1.5, opacity: 0 },
            { scale: 1, opacity: 1, duration: 0.4, ease: 'back.out(1.7)' }
        );
    };

    useGSAP(() => {
        // Header Reveal
        gsap.from('.pricing-header-text', {
            y: 50,
            opacity: 0,
            duration: 1,
            stagger: 0.1,
            ease: 'power3.out',
            scrollTrigger: {
                trigger: containerRef.current,
                start: 'top 70%'
            }
        });



    }, { scope: containerRef });

    // Floating Geoms Animation
    useGSAP(() => {
        gsap.to('.pricing-float-geom', {
            y: (i) => Math.sin(i + 1) * 20,
            rotation: (i) => (i + 1) * 45,
            duration: (i) => 3 + i,
            repeat: -1,
            yoyo: true,
            ease: 'sine.inOut'
        });
    }, { scope: containerRef });

    // Re-animate when category changes
    useGSAP(() => {
        gsap.fromTo('.pricing-card',
            { y: 50, opacity: 0, scale: 0.95 },
            { y: 0, opacity: 1, scale: 1, duration: 0.5, stagger: 0.1, ease: 'back.out(1.2)' }
        );

        gsap.fromTo('.feature-row',
            { x: -20, opacity: 0 },
            { x: 0, opacity: 1, duration: 0.4, stagger: 0.05, ease: 'power2.out', delay: 0.2 }
        );
    }, { scope: containerRef, dependencies: [category] });



    // ... (existing code top)

    return (
        <>
            <section id="pricing-plans" ref={containerRef} className="hidden lg:block py-24 relative overflow-hidden bg-slate-50/50">
                {/* Unique Mesh Background */}
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-indigo-100/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                    <div className="absolute bottom-0 left-0 w-[800px] h-[800px] bg-violet-100/40 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
                    <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20" />
                </div>

                <div className="container mx-auto px-4 relative z-10">

                    {/* Interactive Header */}
                    <div className="text-center mb-12">
                        <h2 className="pricing-header-text text-4xl md:text-6xl font-black text-slate-900 mb-6 tracking-tight">
                            Choose Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-violet-600">Ambition</span>
                        </h2>
                        <p className="pricing-header-text text-xl text-slate-600 max-w-2xl mx-auto mb-10">
                            Scalable solutions designed to grow with your business journey.
                        </p>

                        {/* Category Toggle (Fixed Width & No Overlap) */}
                        <div className="pricing-header-text inline-flex p-1.5 bg-white rounded-full border border-slate-200 shadow-sm relative min-w-[320px] md:min-w-[400px]">
                            <div
                                className={`absolute top-1.5 bottom-1.5 rounded-full bg-slate-900 transition-all duration-300 ease-out z-0`}
                                style={{
                                    left: category === 'development' ? '6px' : '50%',
                                    width: 'calc(50% - 6px)',
                                }}
                            />
                            <div className="relative z-10 flex w-full">
                                <button
                                    onClick={() => setCategory('development')}
                                    className={`flex-1 px-6 py-3 rounded-full text-sm font-bold transition-colors duration-300 text-center ${category === 'development' ? 'text-white' : 'text-slate-500 hover:text-slate-900'}`}
                                >
                                    Web & App Dev
                                </button>
                                <button
                                    onClick={() => setCategory('growth')}
                                    className={`flex-1 px-6 py-3 rounded-full text-sm font-bold transition-colors duration-300 text-center ${category === 'growth' ? 'text-white' : 'text-slate-500 hover:text-slate-900'}`}
                                >
                                    Digital Growth
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Pricing Cards Grid (Flex for Centering) */}
                    <div className="flex flex-wrap justify-center gap-8 max-w-7xl mx-auto mb-20">
                        {plans[category].map((plan) => (
                            <div
                                key={plan.id}
                                className={`pricing-card group relative p-8 rounded-[2rem] bg-white border border-slate-100 hover:border-indigo-300 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 hover:scale-[1.02] cursor-pointer w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.33%-1.5rem)] min-w-[300px] max-w-md flex flex-col`}
                            >
                                {plan.popular && (
                                    <div className="absolute top-6 right-6 px-3 py-1 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 text-white text-[10px] font-bold uppercase tracking-wider shadow-md animate-pulse z-10">
                                        Popular
                                    </div>
                                )}

                                {/* Card 3D Elements */}
                                <div className="absolute top-[-20px] left-[-20px] w-24 h-24 bg-gradient-to-br from-indigo-500/10 to-transparent rounded-full blur-2xl pointer-events-none" />
                                <div className="absolute bottom-[-20px] right-[-20px] w-32 h-32 bg-gradient-to-tl from-purple-500/10 to-transparent rounded-full blur-2xl pointer-events-none" />

                                {/* Floating Shapes */}
                                <div className="pricing-float-geom absolute top-[10%] right-[10%] w-8 h-8 rounded-lg border border-indigo-200/50 bg-indigo-50/20 rotate-12 pointer-events-none" />
                                <div className="pricing-float-geom absolute bottom-[20%] left-[10%] w-6 h-6 rounded-full border border-violet-200/50 bg-violet-50/20 pointer-events-none" />

                                <h3 className="text-xl font-bold text-slate-900 mb-2">{plan.name}</h3>
                                <div className="text-sm text-indigo-500 font-bold mb-6">{plan.subtitle}</div>

                                <div
                                    className="flex flex-col mb-6 relative group/price cursor-pointer"
                                    onClick={(e) => {
                                        if (!revealedPrices[plan.id]) {
                                            triggerReveal(plan.id, e);
                                        }
                                    }}
                                >
                                    {/* Burst Elements */}
                                    <div className={`price-burst-${plan.id} absolute inset-0 pointer-events-none`}>
                                        {[...Array(6)].map((_, i) => (
                                            <div key={i} className="absolute left-1/2 top-1/2 w-2 h-2 rounded-full bg-indigo-500 opacity-0" />
                                        ))}
                                    </div>

                                    {plan.price !== 'Contact Us' && (
                                        <span className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-1">Starting from</span>
                                    )}

                                    <div className="relative">
                                        <div className={`transition-all duration-500 ${revealedPrices[plan.id] ? 'filter-none' : 'blur-md select-none'}`}>
                                            <span className={`text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r ${plan.gradient}`}>
                                                {plan.price}
                                            </span>
                                        </div>

                                        {/* Click to Reveal Overlay */}
                                        {!revealedPrices[plan.id] && (
                                            <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                                                <div className="px-3 py-1 bg-white/80 rounded-full border border-white/60 shadow-sm animate-pulse backdrop-blur-sm">
                                                    <span className="text-[10px] font-bold text-slate-900 uppercase tracking-widest">Click to Reveal</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <p className="text-slate-500 mb-8 pb-8 border-b border-slate-100 min-h-[60px]">{plan.desc}</p>

                                {plan.price === 'Contact Us' ? (
                                    <Link
                                        href="/contact"
                                        className={`w-full py-3.5 rounded-xl font-bold transition-all duration-300 mt-auto flex items-center justify-center ${plan.btnStyle}`}
                                    >
                                        Contact Sales
                                    </Link>
                                ) : (
                                    <Link
                                        href="/contact#contact-methods"
                                        className={`w-full py-3.5 rounded-xl font-bold transition-all duration-300 mt-auto flex items-center justify-center ${plan.btnStyle}`}
                                    >
                                        Get Started
                                    </Link>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Feature Comparison Table */}
                    <div className="max-w-5xl mx-auto">
                        <div className="flex flex-col items-center gap-6 mb-10">
                            <h3 className="text-2xl font-bold text-center text-slate-900">Detailed Feature Comparison</h3>

                            {/* Table Toggle */}
                            <div className="inline-flex p-1 bg-white rounded-full border border-slate-200 shadow-sm relative min-w-[280px]">
                                <div
                                    className={`absolute top-1 bottom-1 rounded-full bg-slate-100 border border-slate-200 transition-all duration-300 ease-out z-0`}
                                    style={{
                                        left: category === 'development' ? '4px' : '50%',
                                        width: 'calc(50% - 4px)',
                                    }}
                                />
                                <div className="relative z-10 flex w-full">
                                    <button
                                        onClick={() => setCategory('development')}
                                        className={`flex-1 px-4 py-2 rounded-full text-xs font-bold transition-colors duration-300 text-center ${category === 'development' ? 'text-slate-900' : 'text-slate-500 hover:text-slate-900'}`}
                                    >
                                        Web & App Dev
                                    </button>
                                    <button
                                        onClick={() => setCategory('growth')}
                                        className={`flex-1 px-4 py-2 rounded-full text-xs font-bold transition-colors duration-300 text-center ${category === 'growth' ? 'text-slate-900' : 'text-slate-500 hover:text-slate-900'}`}
                                    >
                                        Digital Growth
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="feature-table relative bg-white rounded-[2rem] shadow-xl border border-slate-200 overflow-hidden">
                            {/* data-lenis-prevent fixes unreliable mobile swipe by stopping smooth scroll interference */}
                            <div
                                className="overflow-x-auto custom-scrollbar relative z-10 overscroll-x-contain"
                            >
                                <table className="w-full text-left border-collapse min-w-[600px]">
                                    <thead>
                                        <tr className="bg-slate-50 border-b border-slate-200">
                                            <th className="sticky left-0 bg-slate-50 z-20 p-6 font-bold text-slate-900 w-1/3 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)]">Features</th>
                                            {plans[category].map(plan => (
                                                <th key={plan.id} className="p-6 text-center text-slate-900 font-bold w-1/4">
                                                    {plan.name}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {featuresData[category].map((feature, i) => (
                                            <tr key={i} className="feature-row border-b border-slate-100 hover:bg-slate-50 transition-colors">
                                                <td className="sticky left-0 bg-white group-hover:bg-slate-50 z-20 p-5 px-6 font-medium text-slate-600 border-r border-slate-100 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)]">
                                                    {feature.name}
                                                </td>
                                                {plans[category].map(plan => {
                                                    const val = (feature as any)[plan.id] ?? (feature as any)[plan.id.toLowerCase()] ?? (feature as any)[Object.keys(feature).find(k => k.toLowerCase().includes(plan.id)) || ''];

                                                    return (
                                                        <td key={plan.id} className="p-5 text-center">
                                                            {val ? (
                                                                <div className="mx-auto w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
                                                                    <Icon name="CheckIcon" className="w-4 h-4" />
                                                                </div>
                                                            ) : (
                                                                <span className="text-slate-300">-</span>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {/* 3D Decor for Table */}
                            <div className="absolute top-[-20px] left-[20px] w-16 h-16 border-4 border-indigo-100/50 rounded-xl rotate-12 -z-0 pointer-events-none pricing-float-geom" />
                            <div className="absolute bottom-[40px] right-[-10px] w-20 h-20 border-4 border-violet-100/50 rounded-full -z-0 pointer-events-none pricing-float-geom" />
                        </div>
                    </div>

                </div>
            </section>

            <div className="block lg:hidden">
                <MobilePricingTiers />
            </div>
        </>
    );
};

export default PricingTiers;
